import { StatusProps } from '@assembly-js/design-system';
import moment from 'moment';
import Stripe from 'stripe';

import { Company, PAYMENT_FEE, SubscriptionStatus, User } from 'src/constants';
import { API } from 'src/utils/AmplifyApiUtils';
import {
  AutomaticInvoiceSchema,
  LineItemsSchema,
  ManualInvoiceSchema,
} from 'src/legacy/components/billing/new/types';
import { SeverityLevel } from 'src/legacy/components/UI';
import {
  Invoice,
  InvoiceCollectionMethod,
  InvoiceStatus,
  PaymentStatus,
} from 'src/services/api/invoicesApi';
import {
  InvoiceAdditionalFields,
  InvoiceEntity,
  InvoiceLineItem,
  InvoicePaymentFees,
} from 'src/store/payments/types';
import { DateUtils, FileUtils, S3Utils } from 'src/utils';
import { StripePlanInfo } from 'src/utils/BillingUtils';

export default class InvoiceUtils {
  static MapInvoiceStatusToServeity = (status: string): SeverityLevel => {
    let severityLevel: SeverityLevel = SeverityLevel.low;
    switch (status.toLowerCase()) {
      case InvoiceStatus.Open:
      case InvoiceStatus.Overdue:
        severityLevel = SeverityLevel.medium;
        break;
      case InvoiceStatus.Draft:
        severityLevel = SeverityLevel.neutral;
        break;
      case InvoiceStatus.Paid:
        severityLevel = SeverityLevel.low;
        break;
      case InvoiceStatus.Void:
        severityLevel = SeverityLevel.high;
        break;
      case InvoiceStatus.Uncollectible:
        severityLevel = SeverityLevel.high;
        break;
      case InvoiceStatus.Processing:
        severityLevel = SeverityLevel.medium;
        break;
      default:
        break;
    }
    return severityLevel;
  };

  /**
   * This methods get subtotals for subscriptions line items
   * The difference between this method and GetSubTotalOfLineitem is that this method
   * uses the legacy amount field for the line item.
   * @param lineItems
   * @returns number
   */
  static GetSumOfSubtotals = (lineItems: InvoiceLineItem[]) =>
    lineItems.reduce((sum, currentItem) => {
      const itemTotal = currentItem.quantity * Number(currentItem.rate ?? 0);
      const itemTotalInCents = convertAmountToCents(itemTotal);
      const roundedItemTotal = Number(itemTotalInCents.toFixed(2)) / 100;
      return sum + roundedItemTotal;
    }, 0);

  static GetACHFeeLabel = (amount: number) => {
    if (amount / 100.0 > PAYMENT_FEE.LIMIT) {
      return `$${PAYMENT_FEE.MAX_ACH_FEE}`;
    }
    return `${PAYMENT_FEE.BANK_DEFAULT}%`;
  };

  static GetACHFee = (amount: number) => {
    if (amount / 100.0 > PAYMENT_FEE.LIMIT) {
      return PAYMENT_FEE.MAX_ACH_FEE * 100;
    }
    return amount * (PAYMENT_FEE.BANK_DEFAULT / 100.0);
  };

  static GetSubTotalOfLineitem = (lineItems: LineItemsSchema) =>
    lineItems.reduce((sum, currentItem) => {
      const itemTotal =
        Number(currentItem.quantity) * Number(currentItem.amount ?? 0);
      const itemTotalInCents = convertAmountToCents(itemTotal);
      const roundedItemTotal = Number(itemTotalInCents.toFixed(2)) / 100;
      return sum + roundedItemTotal;
    }, 0);

  static GetDueDate = (invoiceFields: Stripe.Invoice | undefined) => {
    if (invoiceFields && invoiceFields.due_date) {
      return moment.unix(invoiceFields.due_date).format('MMM DD, YYYY');
    }
    if (invoiceFields && invoiceFields.due_date === 0) {
      return 'Today';
    }

    return '';
  };

  static GetTotalAmount = (
    lineItems: LineItemsSchema,
    taxPercentage: string,
  ) => {
    const subTotal = InvoiceUtils.GetSubTotalOfLineitem(lineItems).toString();
    const taxAmount =
      !!subTotal && !!taxPercentage
        ? parseFloat(subTotal) * (parseFloat(taxPercentage) / 100.0)
        : 0;

    // total amount is always in dollars
    return subTotal ? parseFloat(subTotal) + taxAmount : 0;
  };

  /**
   * This method checks if an invoice is issued from a subscription
   * @param invoiceData
   */
  static IsSubscriptionCreatedInvoice = (invoiceData: InvoiceEntity) =>
    [
      'subscription_create',
      'subscription_cycle',
      'subscription_update',
    ].includes(invoiceData?.additionalFields?.billing_reason || '');

  static IsStripeSubscription = (
    sub?: string | Stripe.Subscription | null,
  ): sub is Stripe.Subscription => typeof sub !== 'string';

  static IsOpenSubscriptionInvoice = (
    invoiceData?: Stripe.Invoice | InvoiceAdditionalFields,
  ) =>
    Boolean(
      invoiceData &&
        invoiceData.billing_reason === 'subscription_create' &&
        this.IsStripeSubscription(invoiceData.subscription)
        ? invoiceData.subscription?.status !== SubscriptionStatus.Canceled
        : false,
    );

  static calculatePaymentFees = (
    feeDetails: InvoicePaymentFees | undefined,
    absorbTransactionFees: boolean,
    legacyFeeAmount: number | null,
  ) => {
    let clientPaymentFee = null;
    let paymentFee = null;

    /*
     feeDetails will be null when
      a. for non-paid invoices
      b. for legacy paid invoices
     for these cases we need show legacyFeeAmount
     based on if the transaction fee is absorbed or not
    */
    if (!feeDetails) {
      if (absorbTransactionFees) {
        paymentFee = legacyFeeAmount;
      } else {
        clientPaymentFee = legacyFeeAmount;
      }

      return {
        clientPaymentFee,
        paymentFee,
      };
    }

    const {
      fee_credit_international_perc_amount,
      fee_credit_conversion_perc_amount,
      fee_recurring_perc_amount,
      fee_ach_fixed_amount,
      fee_ach_perc_amount,
      fee_credit_base_perc_amount,
      fee_credit_fixed_amount,
    } = feeDetails;

    // These fees are always paid by internal user
    paymentFee =
      fee_credit_international_perc_amount +
      fee_credit_conversion_perc_amount +
      fee_recurring_perc_amount;

    clientPaymentFee = 0;

    const absorbableFee =
      fee_ach_fixed_amount +
      fee_ach_perc_amount +
      fee_credit_base_perc_amount +
      fee_credit_fixed_amount;

    // if internal user is absorbing payment fees
    // then add absorbable fee into paymentFee
    // else that fee will be paid by client
    // so add absorbable fee into clientPaymentFee
    if (absorbTransactionFees) {
      paymentFee += absorbableFee;
    } else {
      clientPaymentFee += absorbableFee;
    }

    return {
      clientPaymentFee,
      paymentFee,
    };
  };
}

/**
 *
 * @param amount - number
 * @description - Amount in invoices is kept in cents, they are also prone to floating point errors
 * so we need to round off and convert them to cents before sending to stripe
 * @returns
 */
export const convertAmountToCents = (amount: number) => {
  return Math.round(amount * 100);
};

/**
 *
 * @param existingInvoice {Invoice}
 * @param EnableInvoiceAddressCustomFields {boolean} - Flag to conditionally include address
 * @description invoiceEntityToInvoiceSchema will take an existing invoice entity and transform it into InvoiceSchema
 * InvoiceSchema is used by create and update methods for invoices
 */
export const invoiceEntityToInvoiceSchema = (
  existingInvoice: Invoice,
  EnableInvoiceAddressCustomFields = false,
): ManualInvoiceSchema | AutomaticInvoiceSchema => {
  const isAutoChargeInvoice =
    existingInvoice.collectionMethod ===
    InvoiceCollectionMethod.ChargeAutomatically;

  const defaultSchemaValues = {
    recipientId: existingInvoice.recipientId,
    taxPercentage: existingInvoice.taxPercentage.toString(),
    attachmentKeys: existingInvoice.attachmentKeys,
    memo: existingInvoice.memo,
    lineItems: existingInvoice.lineItems,
    paymentPreferences: existingInvoice.paymentPreferences,
    totalAmount: existingInvoice.total,
    status: InvoiceStatus.Draft,
    currency: existingInvoice.currency,
    ...(EnableInvoiceAddressCustomFields && {
      address: existingInvoice.address,
    }),
  };

  if (isAutoChargeInvoice) {
    const payload: AutomaticInvoiceSchema = {
      ...defaultSchemaValues,
      collectionMethod: InvoiceCollectionMethod.ChargeAutomatically,
      paymentSourceId: '',
    };
    return payload;
  } else {
    const existingInvoiceIssue = moment(existingInvoice.createdAt);
    const existingInvoiceDue = moment(existingInvoice.dueDate);
    const existingInvoiceDueDateCount = existingInvoiceDue.diff(
      existingInvoiceIssue,
      'days',
      true,
    );
    const dueDate = moment()
      .add(Math.round(existingInvoiceDueDateCount), 'days')
      .toISOString();

    const payload: ManualInvoiceSchema = {
      ...defaultSchemaValues,
      collectionMethod: InvoiceCollectionMethod.SendInvoice,
      dueDate: dueDate,
      dueDays: '',
      paymentSourceId: null,
    };
    return payload;
  }
};

/**
 * This method will handle the download of invoice
 * @param param.fileKey - string - fileKey of the invoice
 * @param param.invoiceNumber - string - invoice number is used to name the file
 */
export const handleDownloadInvoice = async ({
  fileKey,
  invoiceNumber,
  invoiceId,
}: {
  fileKey: string;
  invoiceNumber: string;
  invoiceId?: string;
}) => {
  const response = await S3Utils.downloadFileWithSignedKey(
    fileKey,
    '/v0/invoices/download/signedUrl',
  );

  const fileName = `Invoice-${invoiceNumber}`;
  FileUtils.downloadFile(response.data, fileKey, fileName);

  emitInvoiceDownloadedAuditEvent({ invoiceId, invoiceNumber });
};

export const downloadInvoiceReceipt = async ({
  receiptKey,
  receiptNumber,
  invoiceId,
}: {
  receiptKey: string;
  receiptNumber: string;
  invoiceId?: string;
}) => {
  const response = await S3Utils.downloadFileWithSignedKey(
    receiptKey,
    '/v0/invoices/download/signedUrl',
  );

  const fileName = `Receipt-${receiptNumber}`;
  FileUtils.downloadFile(response.data, receiptKey, fileName);

  emitInvoiceDownloadedAuditEvent({
    invoiceId,
    invoiceNumber: receiptNumber,
    isReceipt: true,
  });
};

const emitInvoiceDownloadedAuditEvent = ({
  invoiceId,
  invoiceNumber,
  isReceipt = false,
}: {
  invoiceId?: string;
  invoiceNumber: string;
  isReceipt?: boolean;
}) => {
  const documentType = isReceipt ? 'Receipt' : 'Invoice';
  API.post('AppAPI', '/events', {
    body: {
      eventType: 'invoiceDownloaded',
      eventDescription: `${documentType} ${invoiceNumber} downloaded`,
      context: {
        ...(invoiceId && { invoiceId }),
        ...(isReceipt
          ? { receiptNumber: invoiceNumber }
          : { number: invoiceNumber }),
      },
    },
  }).catch(() => {
    // fire-and-forget — audit failures must not affect download UX
  });
};

export const getIntervalFromPlan = (plan: StripePlanInfo) => {
  if (plan.interval === 'month' && plan.intervalCount === 3) {
    return 'quarter';
  }
  if (plan.interval === 'month' && plan.intervalCount === 6) {
    return 'biannual';
  }
  if (plan.interval === 'month' && plan.intervalCount === 24) {
    return '2 year';
  }

  return plan.interval;
};

/**
 * @param invoice {Invoice}
 * @description Takes in an invoice and returns the status of the invoice.
 * While invoices can be of any status that's defined in the InvoiceStatus enum,
 * Processing and Overdue aren't actual statuses. Instead, they are states that an invoice can be in.
 * - Processing: When the invoice is open and has a payment that is processing.
 * - Overdue: When the invoice is open but past its due date.
 * This hook checks for these conditions and returns the appropriate state-based status.
 * @returns {InvoiceStatus}
 */

export const getInvoiceStatus = (invoice: Invoice) => {
  const lastPayment = invoice.payment?.at(-1);

  if (
    lastPayment?.status === PaymentStatus.Processing &&
    invoice.status === InvoiceStatus.Open
  ) {
    return InvoiceStatus.Processing;
  }

  if (!invoice.dueDate) {
    return invoice.status; // Not overdue if no due date
  }

  const isInvoiceOverdue =
    DateUtils.dateComparator(invoice.dueDate, new Date().toISOString()) === -1;

  if (isInvoiceOverdue && invoice.status === InvoiceStatus.Open) {
    return InvoiceStatus.Overdue;
  }

  return invoice.status;
};

export const mapInvoiceStatusToStatusVariant = (
  status: string,
): StatusProps['status'] => {
  switch (status) {
    case InvoiceStatus.Draft:
    case InvoiceStatus.Void:
    case InvoiceStatus.Uncollectible:
    case InvoiceStatus.Processing:
      return 'neutral';
    case InvoiceStatus.Open:
      return 'info';
    case InvoiceStatus.Paid:
      return 'success';
    case InvoiceStatus.Overdue:
      return 'warning';
    default:
      return 'neutral';
  }
};

export const getRecipientData = (
  clientUsers: Map<string, User>,
  companies: Map<string, Company>,
  recipientId: string,
  clientId?: string,
  companyId?: string,
) => {
  // For multi-company invoices, we have both clientId and companyId
  const client = clientId ? clientUsers.get(clientId) : null;
  const company = companyId ? companies.get(companyId) : null;

  // If we have both client and company (multi-company scenario)
  if (client && company) {
    return {
      firstName: client.fields.givenName,
      lastName: client.fields.familyName,
      email: client.fields.email,
      companyName: company.fields.name,
      companyIds: client.fields.companyIds,
      type: 'client-company',
      recipientStatus: client.entityStatus,
      avatarImageURL: client.fields.avatarImageUrl,
      fallbackColor: client.fields.fallbackColor,
      companyAvatar: {
        name: company.fields.name,
        src: company.fields.avatarImageURL,
        fallbackColor: company.fields.fallbackColor,
      },
    };
  }

  // If we only have clientId (single client)
  if (client) {
    return {
      firstName: client.fields.givenName,
      lastName: client.fields.familyName,
      email: client.fields.email,
      type: 'client',
      recipientStatus: client.entityStatus,
      avatarImageURL: client.fields.avatarImageUrl,
      fallbackColor: client.fields.fallbackColor,
    };
  }

  // If we only have companyId (company-only invoice)
  if (company) {
    return {
      companyName: company.fields.name,
      type: 'company',
      recipientStatus: company.entityStatus,
      avatarImageURL: company.fields.avatarImageURL,
      fallbackColor: company.fields.fallbackColor,
    };
  }

  // Fallback to legacy recipientId lookup
  const invoiceCompany = companies.get(recipientId);
  const isCompanyInvoice = !!invoiceCompany;

  if (isCompanyInvoice) {
    return {
      companyName: invoiceCompany ? invoiceCompany.fields.name : '',
      type: 'company',
      recipientStatus: invoiceCompany ? invoiceCompany.entityStatus : '',
      avatarImageURL: invoiceCompany
        ? invoiceCompany.fields.avatarImageURL
        : '',
      fallbackColor: invoiceCompany ? invoiceCompany.fields.fallbackColor : '',
    };
  } else {
    const fetchedClientUser = clientUsers.get(recipientId);
    return {
      firstName: fetchedClientUser ? fetchedClientUser.fields.givenName : '',
      lastName: fetchedClientUser ? fetchedClientUser.fields.familyName : '',
      email: fetchedClientUser ? fetchedClientUser.fields.email : '',
      type: 'client',
      recipientStatus: fetchedClientUser ? fetchedClientUser.entityStatus : '',
      avatarImageURL: fetchedClientUser
        ? fetchedClientUser.fields.avatarImageUrl
        : '',
      fallbackColor: fetchedClientUser
        ? fetchedClientUser.fields.fallbackColor
        : '',
    };
  }
};
